import { formatUnits } from '@ethersproject/units';
import { BIG_ZERO } from 'app/constants';
import { LTV } from 'app/features/util/ltv';
import { convertCurrency } from 'app/functions/silo/calculations';
import { DecoratedSilo, SiloAsset } from 'queries/decoratedSilo';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { adjustLtvLimit } from '../features/silo/silo/shared';
import { BigIntWithDecimals, BigNumberWithDecimals, fromBigIntWithDecimals } from '@silo/types';

interface GetMaxAmountParams {
  decoratedSilo: DecoratedSilo;
  selectedAsset: SiloAsset;
  currentLTV: LTV | null;
}

export type GetMaxAmount = (params: GetMaxAmountParams) => MaxAmount;

export type MaxAmount = {
  type: 'ltvLimit' | 'invalid' | 'liquidity' | 'userBalance' | 'protocolBalance';
  value: BigNumberWithDecimals;
};

interface UseMaxAmountProps {
  getMaxAmount: () => MaxAmount;
  setTokenAmount: Dispatch<SetStateAction<string>>;
  priceInUSD: BigIntWithDecimals | null;
}

export interface UseMaxAmount {
  maxAmount: MaxAmount;
  maxAmountInUSD: BigNumberWithDecimals | null;
  setMaxAmount: () => void;
}

export const useMaxTokenAmount = ({ getMaxAmount, priceInUSD, setTokenAmount }: UseMaxAmountProps): UseMaxAmount => {
  const maxAmount = useMemo<MaxAmount>(() => {
    const { type, value } = getMaxAmount();

    if (value.bigNumber.lt(BIG_ZERO)) {
      return {
        type,
        value: {
          bigNumber: BIG_ZERO,
          decimals: value.decimals,
        },
      };
    }

    return { type, value };
  }, [getMaxAmount]);

  /**
   * @todo BigNumber-to-bigint
   */
  // const priceInUSD = usePriceInUSD(selectedAsset.priceInNative.toBigInt());

  const maxAmountInUSD = useMemo(() => {
    if (!priceInUSD) return null;

    return convertCurrency(maxAmount.value, fromBigIntWithDecimals(priceInUSD));
  }, [priceInUSD, maxAmount]);

  const setMaxAmount = (): void => {
    const displayedMax = getDisplayedMax(maxAmount);

    setTokenAmount(serializeAmount(displayedMax));
  };

  return {
    maxAmount,
    maxAmountInUSD,
    setMaxAmount,
  };
};

export const serializeAmount = (value: BigNumberWithDecimals): string =>
  value.bigNumber.isZero() ? '' : formatUnits(value.bigNumber, value.decimals);

export const determineMax = (maxAmounts: MaxAmount[]) =>
  maxAmounts.reduce((globalMax, currentMax) =>
    globalMax.value.bigNumber.lt(currentMax.value.bigNumber) ? globalMax : currentMax
  );

export const getDisplayedMax = (maxAmount: MaxAmount): BigNumberWithDecimals => {
  if (maxAmount.type === 'ltvLimit') return adjustLtvLimit(maxAmount.value);

  return maxAmount.value;
};
